import { useEffect, useCallback, useState, useRef } from 'preact/hooks'
import { useAssetWebsterChat } from '@/hooks/useAssetWebsterChat'
import { useLandingPage } from '@/hooks/useLandingPage'
import { useLandingPageSignalR } from '@/hooks/useLandingPageSignalR'
import { STATUS_MESSAGES } from '@/hooks/useStreamingStatus'
import { describeGenerationFailure, repeatedChatLine, toGenerationFailure } from '@/utils/generationFailure'
import type { GenerationFailureDto } from '@/types/api-generated'
import type { AIConversationSessionDto } from '@/types/aiChat'
import type {
    LandingPageDesignAnswerDto,
    LandingPageDesignQuestionDto,
    LandingPageGenerateRequestDto,
} from '@/types/landingPage'
import type { OpenDesignRound, WebsterAttachment } from '@/types/webster'

/** How often a working turn re-reads the page for a design round whose live event was missed. */
const DESIGN_ROUND_RECHECK_MS = 5000

interface UseLandingPageChatParams {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    /** Fires after a chat-driven rebuild (or thank-you rebuild) finishes
     *  applying. Lets the parent re-fetch the WebAsset so DraftUpdatedAt
     *  stays current — the Save-as-Version button reads it to decide
     *  whether the draft is ahead of the latest version. Return value is
     *  ignored; if a Promise is returned it is awaited. */
    onRebuilt?: () => unknown
}


export function useLandingPageChat({ clientCompanyId, workspaceId, campaignId, webId, onRebuilt: onRebuiltExternal }: UseLandingPageChatParams) {
    const landingPageHook = useLandingPage({ clientCompanyId, workspaceId, campaignId, webId })

    // Shared chat lifecycle. LP creates its session server-side as part of
    // generation, so no createSession factory — sendMessage no-ops until the
    // session id is captured from the LP DTO.
    const assetChat = useAssetWebsterChat({
        clientCompanyId,
        workspaceId,
        initialSessionId: null,
    })
    const { chat, streamingStatus } = assetChat
    const { streamMsgId, resultHandledRef, hadRebuildRef, updateStatus, beginStreaming } = streamingStatus

    const [thankYouGenerating, setThankYouGenerating] = useState(false)
    // The ref closes the gap before Preact renders; a 202 only accepts the job,
    // so keep the guard until a terminal event or a server re-read settles it.
    const generationActiveRef = useRef(false)
    const requestPendingRef = useRef(false)
    const [generationActive, setGenerationActive] = useState(false)
    const [generationRequestError, setGenerationRequestError] = useState<GenerationFailureDto | null>(null)
    const latestChatRef = useRef(chat)
    latestChatRef.current = chat

    const pageGenerationStatus = landingPageHook.landingPage?.GenerationStatus
    useEffect(() => {
        if (pageGenerationStatus === 'Completed') setGenerationRequestError(null)
    }, [pageGenerationStatus])

    const finishGeneration = useCallback((failed = false, error?: GenerationFailureDto | string | null) => {
        generationActiveRef.current = false
        setGenerationActive(false)
        const currentChat = latestChatRef.current
        // Remove old orphaned indicators too, but preserve all user and assistant content.
        const messages = currentChat.messages ?? []
        for (const message of messages) {
            if (message.role === 'assistant' && !message.content && message.streamStatus) {
                currentChat.removeMessage(message.id)
            }
        }
        const active = messages.find(message => message.id === streamMsgId.current)
        if (!active || (active.role === 'assistant' && !active.content)) currentChat.removeMessage(streamMsgId.current)
        currentChat.setGenerating(false)
        if (failed) {
            const failure = toGenerationFailure(error) ?? { Code: 'Unknown', Parameters: {} }
            setGenerationRequestError(failure)
            const line = describeGenerationFailure(failure).chatLine
            // Each Retry that fails the same way bumps a count on one line instead of adding another.
            const previous = [...messages].reverse().find(message => message.id !== streamMsgId.current && message.content)
            const repeated = previous?.role === 'assistant' ? repeatedChatLine(previous.content, line) : null
            if (previous && repeated) currentChat.updateStreamingMessage(previous.id, { content: repeated })
            else currentChat.addAssistantMessage(line)
        }
    }, [streamMsgId])

    // Design questions Webster is waiting on. Seeded live by SignalR and, on a reload or
    // a colleague opening the page mid-round, from the LP DTO — both paths matter,
    // because the asking turn ends and the answer can arrive from a different session.
    const [designQuestions, setDesignQuestions] = useState<LandingPageDesignQuestionDto[]>([])
    const [designRound, setDesignRound] = useState<{ round: number; maxRounds: number } | null>(null)
    // The round being sent. It holds the card on screen, disabled, until the chat shows the round
    // answered: the page drops its pending questions a moment before the transcript catches up.
    const [answering, setAnswering] = useState<LandingPageDesignQuestionDto[] | null>(null)
    // Counts question rounds as they arrive, so a send can tell whether a newer one came in behind it.
    const roundsArrivedRef = useRef(0)
    // A round can reach us twice, from a page re-read and then its own SignalR event (ORB-958), and a
    // double count would make a send think a newer round arrived and never start the build. A round is
    // known by its number, not its wording: a later round may ask the same questions again.
    const lastRoundRef = useRef<number | null>(null)
    const countRound = (round: number) => {
        if (round === lastRoundRef.current) return
        lastRoundRef.current = round
        roundsArrivedRef.current += 1
    }

    // Track the in-flight API call so onStreamResult can wait for it
    const apiCallRef = useRef<Promise<void | boolean | AIConversationSessionDto | null> | null>(null)

    // ---- Agent-stream events (LP first generation) ----------------------

    const onThinking = useCallback(() => {
        updateStatus('thinking', STATUS_MESSAGES.thinking)
    }, [updateStatus])

    const onContent = useCallback(() => {
        updateStatus('content', STATUS_MESSAGES.content)
    }, [updateStatus])

    const onToolUse = useCallback(() => {
        updateStatus('tool_use', STATUS_MESSAGES.tool_use)
    }, [updateStatus])

    const onToolResult = useCallback(() => {
        updateStatus('thinking', STATUS_MESSAGES.thinking)
    }, [updateStatus])

    const onStreamResult = useCallback(async () => {
        // Capture whether the conversational response already finalized this turn's
        // chat bubble BEFORE we flip resultHandledRef. If so, we're in chat-rebuild
        // mode: the personalized response is a finalized bubble above the working
        // indicator, so we just remove the indicator — no need to write the same
        // content into a second bubble.
        const conversationalResponseFinalizedThisTurn = resultHandledRef.current
        resultHandledRef.current = true

        // Wait for the API call to finish before loading data below — could be
        // createAndGenerate (returns void) or aiChatSendMessage (returns a session
        // DTO). Nothing is read off the result: the thank-you nudge is derived from
        // landing-page state (see suggestThankYouGeneration in the return) rather
        // than pushed back on the session, so first-generation lights it up too.
        if (apiCallRef.current) {
            try {
                await apiCallRef.current
            } catch (err) {
                console.error('API call failed during stream result:', err)
            }
            apiCallRef.current = null
        }

        if (generationActiveRef.current) finishGeneration()

        // Phase 1: Finalize the chat presentation. Two cases:
        if (conversationalResponseFinalizedThisTurn) {
            // Conversational response already finalized the bubble this turn. Only
            // remove if it's the transient working indicator (rebuild-prefixed) —
            // for first-gen with AI completion the conversationalResponse reused
            // the original stream id, and that bubble holds the message we want
            // the user to see.
            if (streamMsgId.current.startsWith('rebuild-')) {
                chat.removeMessage(streamMsgId.current)
            }
            chat.setGenerating(false)
        } else {
            // First-generation (and any non-conversational) flow: the visible Webster reply
            // is the agent's inline <message> closer, delivered + persisted via the
            // assistantMessageAppended SignalR event (same as email). So just tear down the
            // working bubble here. Finalizing it with a canned "I've generated your landing
            // page!" produced a stray, never-persisted message that duplicated the real
            // closer and vanished on reload — legacy from before LP adopted the inline
            // envelope. Lifecycle-only, mirroring useEmailAssetChat's onRebuilt.
            chat.removeMessage(streamMsgId.current)
            chat.setGenerating(false)
        }

        // Phase 2: Load data during blast-off animation (~4.5s window).
        const [freshLp] = await Promise.all([landingPageHook.load(), landingPageHook.loadDraft(), landingPageHook.loadContext()])
        // Also refresh the WebAsset so DraftUpdatedAt stays current — the
        // Save-as-Version gate reads it to decide whether the draft has moved
        // ahead of the latest version. Without this, edit flows that resolve
        // through the result-stream path (rather than the Rebuilt SignalR
        // event) leave the asset stale and Save stays disabled.
        await onRebuiltExternal?.()

        // Capture session id (server may create it after firing result event)
        if (freshLp?.ConversationSessionId) {
            assetChat.captureSessionId(freshLp.ConversationSessionId)
        } else if (!assetChat.sessionId) {
            // Session not ready yet — poll briefly until the server creates it
            const pollForSession = async () => {
                for (let i = 0; i < 5; i++) {
                    await new Promise(r => setTimeout(r, 2000))
                    const polledLp = await landingPageHook.load()
                    if (polledLp?.ConversationSessionId) {
                        assetChat.captureSessionId(polledLp.ConversationSessionId)
                        return
                    }
                }
            }
            pollForSession()
        }
    }, [landingPageHook, chat, assetChat, streamMsgId, resultHandledRef, onRebuiltExternal, finishGeneration])

    const onStreamError = useCallback((message: GenerationFailureDto | string) => {
        console.error('Landing page generation error:', message)
        if (generationActiveRef.current) {
            finishGeneration(true, message)
            setThankYouGenerating(false)
            void landingPageHook.load()
            return
        }

        // The agent-stream `error` event is generic — it can fire for LP first-gen,
        // chat-driven rebuilds, OR TY-only background generation (which has no chat
        // turn in flight). Only touch the chat if a chat turn is actually active;
        // otherwise we'd corrupt the previously-finalized bubble's content.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: describeGenerationFailure(message).chatLine,
                streamStatus: undefined,
            })
            chat.setGenerating(false)
            chat.finalizeStreamingMessage(streamMsgId.current)
        }

        // Defensive cleanup for non-chat surfaces driven by an in-flight generation.
        // The dedicated `generationFailed` / `thankYouGenerationFailed` events also
        // do this, but those don't fire for every error path — the agent service
        // can emit `error` without the queue trigger's onFailure handler running
        // (e.g. when the AI service returns the error inline rather than throwing).
        setThankYouGenerating(false)
        landingPageHook.load()
    }, [chat, streamMsgId, landingPageHook, finishGeneration])

    const onStreamDone = useCallback(() => {
        // Safety net — but only if onStreamResult hasn't already handled it.
        if (resultHandledRef.current || generationActiveRef.current) return
        chat.setGenerating(false)
        chat.finalizeStreamingMessage(streamMsgId.current)
    }, [chat, streamMsgId, resultHandledRef])

    // ---- Thank-you sub-stream -------------------------------------------

    const onThankYouGenerating = useCallback(() => {
        setThankYouGenerating(true)
    }, [])

    const onThankYouResult = useCallback(async () => {
        // Stop and finalize the chat thinking indicator the moment the thank-you page
        // returns. The generic stream events have no completion of their own for the
        // thank-you turn, so without this the bubble would keep ticking indefinitely.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: "I've created your thank you page. Take a look at the preview.",
                streamStatus: undefined,
            })
            chat.finalizeStreamingMessage(streamMsgId.current)
            chat.setGenerating(false)
        }
        await landingPageHook.loadThankYouDraft()
        await landingPageHook.load()
        setThankYouGenerating(false)
    }, [landingPageHook, chat, streamMsgId])

    const onThankYouRebuilding = useCallback(() => {
        setThankYouGenerating(true)
    }, [])

    const onThankYouRebuilt = useCallback(async () => {
        // Mirror onThankYouResult: finalize the chat indicator so the timer stops when
        // a thank-you rebuild completes.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: "I've updated your thank you page. Take a look at the preview.",
                streamStatus: undefined,
            })
            chat.finalizeStreamingMessage(streamMsgId.current)
            chat.setGenerating(false)
        }
        await Promise.all([landingPageHook.loadThankYouDraft(), landingPageHook.load()])
        await onRebuiltExternal?.()
        setThankYouGenerating(false)
    }, [landingPageHook, onRebuiltExternal, chat, streamMsgId])

    const onGenerationFailed = useCallback(async (code: string, reference?: string | null) => {
        // The event carries only the code and reference; the page holds the rest, like the font's name.
        // When the page itself is gone the event is all there is, reference included.
        const page = await landingPageHook.load()
        const fromEvent = toGenerationFailure(code)
        const failure = page?.GenerationFailure ?? (fromEvent ? { ...fromEvent, Reference: reference ?? null } : null)
        if (generationActiveRef.current) {
            finishGeneration(true, failure)
            setThankYouGenerating(false)
            return
        }
        // The agent's raw `error` event can close the turn first, as Unknown. Now the cause is known,
        // correct that line rather than leave the generic one standing.
        if (!failure) return
        setGenerationRequestError(failure)
        const currentChat = latestChatRef.current
        if (currentChat.generating) {
            // A chat-driven rebuild that failed before the agent ran sends no stream error to close its bubble.
            currentChat.updateStreamingMessage(streamMsgId.current, {
                content: describeGenerationFailure(failure).chatLine,
                streamStatus: undefined,
            })
            currentChat.setGenerating(false)
            currentChat.finalizeStreamingMessage(streamMsgId.current)
            return
        }
        const unknownLine = describeGenerationFailure(null).chatLine
        const withContent = [...(latestChatRef.current.messages ?? [])].filter(message => message.content)
        const last = withContent[withContent.length - 1]
        if (last?.role !== 'assistant' || !last.content?.startsWith(unknownLine)) return
        const corrected = describeGenerationFailure(failure).chatLine
        // A retry into the same cause bumps the count on the line before instead of adding a second one.
        const before = withContent[withContent.length - 2]
        const repeated = before?.role === 'assistant' ? repeatedChatLine(before.content, corrected) : null
        if (before && repeated) {
            latestChatRef.current.updateStreamingMessage(before.id, { content: repeated })
            latestChatRef.current.removeMessage(last.id)
        } else {
            latestChatRef.current.updateStreamingMessage(last.id, { content: corrected })
        }
    }, [landingPageHook, finishGeneration, streamMsgId])

    const onThankYouGenerationFailed = useCallback((message: string) => {
        console.error('Thank you page generation error:', message)
        landingPageHook.load()
        setThankYouGenerating(false)
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: 'Sorry, something went wrong generating the thank you page. Please try again.',
                streamStatus: undefined,
            })
            chat.finalizeStreamingMessage(streamMsgId.current)
            chat.setGenerating(false)
        }
    }, [landingPageHook, chat, streamMsgId])

    // ---- Chat-rebuild events --------------------------------------------
    // Mostly delegated to the shared hook; LP needs to refresh draft HTML on
    // rebuilt and use a custom rebuildError message.

    const onRebuilt = useCallback(async () => {
        // Webster's text reply already arrived via onConversationalResponse (fired before
        // the rebuild was enqueued) and finalized its own bubble; assetChat.onRebuilt
        // removes the transient rebuild-status bubble that onRebuilding opened beneath
        // it. We also refresh the draft HTML and WebAsset so DraftUpdatedAt is current.
        hadRebuildRef.current = true
        assetChat.onRebuilt()
        await Promise.all([landingPageHook.loadDraft(), landingPageHook.load()])
        await onRebuiltExternal?.()
    }, [assetChat, landingPageHook, hadRebuildRef, onRebuiltExternal])

    const onRebuildError = useCallback((message: string) => {
        console.error('Landing page rebuild error:', message)
        chat.updateStreamingMessage(streamMsgId.current, {
            content: 'Sorry, something went wrong updating your page. Please try again.',
            streamStatus: undefined,
        })
        chat.setGenerating(false)
        chat.finalizeStreamingMessage(streamMsgId.current)
    }, [chat, streamMsgId])

    // Subscribe to SignalR generation events
    useLandingPageSignalR({
        webId,
        onThinking,
        onContent,
        onToolUse,
        onToolResult,
        onResult: onStreamResult,
        onError: onStreamError,
        onDone: onStreamDone,
        onThankYouGenerating,
        onThankYouResult,
        onGenerationFailed,
        onThankYouGenerationFailed,
        onAnalyzing: assetChat.onAnalyzing,
        onRebuilding: assetChat.onRebuilding,
        onRebuilt,
        onThankYouRebuilding,
        onThankYouRebuilt,
        onRebuildError,
        onConversationalResponse: (content, willRebuild, modelId, outputTokens, rebuildMode) => {
            assetChat.onConversationalResponse(content, willRebuild, modelId, outputTokens, rebuildMode)
            // A turn that rebuilds nothing can still have saved a page setting, the privacy policy URL (ORB-1145), and
            // with it a new ETag. Read both again, or the sidebar's next save sends the old URL back, or a stale ETag.
            // A rebuild reads them when it finishes. The server saves before it replies, so the write is already there.
            if (!willRebuild) {
                void landingPageHook.loadBehindSaves()
                void landingPageHook.loadContext()
            }
        },
        onAssistantMessageAppended: assetChat.onAssistantMessageAppended,
        // Multi-user turn-lock: chatStateChanged updates lockedByOther state for
        // spectators; userMessageAppended renders peer messages in real time so
        // the chat conveys "Joel just asked X" without waiting for a refresh.
        onChatStateChanged: assetChat.onChatStateChanged,
        onUserMessageAppended: assetChat.onUserMessageAppended,
        onDesignQuestions: (event) => {
            if (event.WebId !== webId) return
            countRound(event.Round)
            generationActiveRef.current = false
            setGenerationActive(false)
            // Webster is waiting on the user now, not working, so the turn's working bubble goes.
            chat.removeMessage(streamMsgId.current)
            chat.setGenerating(false)
            setDesignQuestions(event.Questions)
            setDesignRound({ round: event.Round, maxRounds: event.MaxRounds })
            // Refetch the page. Parking wrote a new ETag and flipped the status to
            // AwaitingInput, and the copy in state is from before that — so without this
            // the answer submits against a stale ETag and Cosmos rejects it with a 412.
            // The server persists before it broadcasts, so the write is already committed
            // by the time this event arrives.
            landingPageHook.load()
        },
    })

    // Mirror whatever the server says is outstanding. Covers the reload case, and clears
    // the form when another user answered first — their answers ended the round, so ours
    // should stop being offered.
    // A round read this way is counted too: its own event can still land later, mid-send, and must
    // not pass for a newer round.
    useEffect(() => {
        const pending = landingPageHook.landingPage?.PendingDesignQuestions ?? []
        setDesignQuestions(pending)
        if (pending.length === 0) setDesignRound(null)
        else countRound(landingPageHook.landingPage!.DesignRoundsAsked)
    }, [landingPageHook.landingPage?.PendingDesignQuestions])

    // A round normally arrives as the DesignQuestions event, but SignalR has no fallback: a dropped
    // connection or a backgrounded tab loses it, and the builder waits on a page nobody is building
    // (ORB-958). So while a turn works, re-read the page; the mirror above opens a parked round's card,
    // and this ends the working state as onDesignQuestions would. A finished page ends the re-reads.
    // Each read is scheduled only once the previous one settles: the reads queue behind saves, so a
    // fixed interval on a slow or offline connection would stack them up and delay the user's next save.
    useEffect(() => {
        if ((!chat.generating && !generationActive) || answering !== null) return
        let stopped = false
        let timer: ReturnType<typeof setTimeout>
        const recheck = async () => {
            // A read started before acceptance can return the previous run's terminal
            // snapshot after the POST settles. Do not start that read in the first place.
            if (requestPendingRef.current) {
                timer = setTimeout(recheck, DESIGN_ROUND_RECHECK_MS)
                return
            }
            const lp = await landingPageHook.loadBehindSaves()
            if (stopped) return
            if (requestPendingRef.current) {
                timer = setTimeout(recheck, DESIGN_ROUND_RECHECK_MS)
                return
            }
            if (lp?.GenerationStatus === 'Completed' || lp?.GenerationStatus === 'Failed') {
                // Chat edits and thank-you turns start with an already completed LP.
                // Only a turn known to be LP generation can be settled by this status.
                if (generationActiveRef.current) {
                    finishGeneration(lp.GenerationStatus === 'Failed', lp.GenerationFailure ?? lp.GenerationError)
                    if (lp.GenerationStatus === 'Completed') {
                        await Promise.all([landingPageHook.loadDraft(), landingPageHook.loadContext()])
                        await onRebuiltExternal?.()
                    }
                }
                return
            }
            if (lp?.GenerationStatus === 'AwaitingInput' && lp.PendingDesignQuestions.length > 0) {
                countRound(lp.DesignRoundsAsked)
                generationActiveRef.current = false
                setGenerationActive(false)
                chat.removeMessage(streamMsgId.current)
                chat.setGenerating(false)
                return
            }
            timer = setTimeout(recheck, DESIGN_ROUND_RECHECK_MS)
        }
        timer = setTimeout(recheck, DESIGN_ROUND_RECHECK_MS)
        return () => {
            stopped = true
            clearTimeout(timer)
        }
    }, [chat.generating, generationActive, answering])

    // The card stays until the answers are accepted, so a refused send leaves them there to retry.
    const submitDesignAnswers = useCallback(async (
        answers: LandingPageDesignAnswerDto[],
        note: string | null,
        letWebsterDecide = false,
    ) => {
        const roundsBeforeSend = roundsArrivedRef.current
        setAnswering(designQuestions)
        try {
            const accepted = await landingPageHook.submitDesignAnswers(answers, note, letWebsterDecide)
            if (!accepted) {
                // Resynced so a retry carries the page's current ETag, and so the card closes if
                // someone else answered first.
                await Promise.all([landingPageHook.load(), assetChat.reloadHistory()])
                return
            }
            await assetChat.reloadHistory()
            // A newer round that arrived while the transcript loaded is Webster asking again: its
            // card stays, and nothing is building.
            if (roundsArrivedRef.current !== roundsBeforeSend) return
            // Cleared here rather than left to the page mirror, which runs after paint and would
            // let the round reopen for a frame once `answering` lets go of it.
            setDesignQuestions([])
            setDesignRound(null)
            generationActiveRef.current = true
            setGenerationActive(true)
            setGenerationRequestError(null)
            beginStreaming()
        } finally {
            setAnswering(null)
        }
    }, [landingPageHook, assetChat, beginStreaming, designQuestions])

    const openQuestions = answering ?? (designQuestions.length > 0 ? designQuestions : null)
    const openDesignRound: OpenDesignRound | null = openQuestions && {
        questions: openQuestions,
        round: designRound?.round,
        maxRounds: designRound?.maxRounds,
        submitting: answering !== null,
        onSubmit: (answers, note) => { void submitDesignAnswers(answers, note) },
        onLetWebsterDecide: () => { void submitDesignAnswers([], null, true) },
    }

    // Initialize: load LP and context in parallel
    useEffect(() => {
        landingPageHook.load()
        landingPageHook.loadContext()
    }, [])

    // The worker creates the chat session when generation starts, after the page first loads, so it
    // is captured whenever it appears rather than once with the page.
    useEffect(() => {
        const conversationSessionId = landingPageHook.landingPage?.ConversationSessionId
        if (conversationSessionId) assetChat.captureSessionId(conversationSessionId)
    }, [landingPageHook.landingPage?.ConversationSessionId])

    // When the LP loads, resume mid-flight state.
    // Deps narrowed to landingPage.Id so this fires exactly once per loaded LP.
    useEffect(() => {
        if (landingPageHook.landingPage?.GenerationStatus === 'Generating') {
            generationActiveRef.current = true
            setGenerationActive(true)
            if (!chat.generating) beginStreaming()
        }
        if (landingPageHook.landingPage?.ThankYouGenerationStatus === 'Generating') {
            setThankYouGenerating(true)
        }
    }, [landingPageHook.landingPage?.Id])

    const isExternalMode = landingPageHook.landingPage?.Mode === 'External'

    /** Resolves false only when the request was sent and refused or failed; a run already under way is not a failure. */
    const requestGeneration = useCallback(async (request: () => Promise<boolean>): Promise<boolean> => {
        if (isExternalMode || generationActiveRef.current || chat.generating || thankYouGenerating
            || landingPageHook.landingPage?.GenerationStatus === 'Generating') return true
        generationActiveRef.current = true
        requestPendingRef.current = true
        setGenerationActive(true)
        setGenerationRequestError(null)
        beginStreaming()
        const pending = request()
        apiCallRef.current = pending
        try {
            if (await pending) return true
            finishGeneration(true)
            return false
        } catch {
            finishGeneration(true)
            return false
        } finally {
            requestPendingRef.current = false
        }
    }, [isExternalMode, chat.generating, thankYouGenerating, landingPageHook.landingPage?.GenerationStatus, beginStreaming, finishGeneration])

    const startGeneration = useCallback(async (settings: LandingPageGenerateRequestDto, prompt?: string): Promise<boolean> => {
        return requestGeneration(() => {
            if (prompt) chat.addUserMessage(prompt)
            return landingPageHook.createAndGenerate(prompt ? { ...settings, Prompt: prompt } : settings)
        })
    }, [landingPageHook, chat, requestGeneration])

    /** Send a message — routes through the shared chat lifecycle. Mirror the
     *  promise into apiCallRef so onStreamResult waits for the send to settle
     *  before it reloads the landing page.
     *  Optional image attachments ride along to Webster (ORB-413). */
    const sendMessage = useCallback(async (prompt: string, attachments?: WebsterAttachment[]) => {
        if (isExternalMode) return
        const promise = assetChat.sendMessage(prompt, attachments)
        apiCallRef.current = promise
        await promise
    }, [assetChat, isExternalMode])

    // A font directive typed in chat: post the user's message + a local confirmation
    // bubble, and apply the font (re-bakes the draft) — no AI rebuild.
    const applyFontFromChat = useCallback(async (userMessage: string, heading: string | null, body: string | null) => {
        chat.addUserMessage(userMessage)
        const ok = await landingPageHook.applyFont(heading, body)
        if (ok) {
            const parts = [heading ? `headings to ${heading}` : null, body ? `body text to ${body}` : null].filter(Boolean)
            chat.addAssistantMessage(
                parts.length ? `Done — set the ${parts.join(' and the ')}. Fine-tune anytime in the Fonts panel.` : 'Reverted to the default fonts.',
            )
        } else {
            chat.addAssistantMessage(`I couldn't load that font — try a different name, or upload a .woff2 in the Fonts panel.`)
        }
    }, [chat, landingPageHook])

    const loadPreview = useCallback(async () => {
        if (isExternalMode) return
        await landingPageHook.loadDraft()
    }, [landingPageHook, isExternalMode])

    /** Retry a failed generation */
    const retryGeneration = useCallback(async () => {
        await requestGeneration(() => landingPageHook.regenerate())
    }, [landingPageHook, requestGeneration])

    /** Rebuild the page from the brief, taking the knowledge base's brand as it is now. */
    const rebuildWithCurrentBrand = useCallback(async () => {
        await requestGeneration(() => landingPageHook.regenerate(undefined, true))
    }, [landingPageHook, requestGeneration])

    /** Trigger explicit thank you page generation */
    const regenerateThankYou = useCallback(async (prompt?: string) => {
        if (isExternalMode) return
        // setThankYouGenerating(true) also retracts the nudge — it's one of the
        // terms the suggestThankYouGeneration derivation reads.
        setThankYouGenerating(true)
        // Open a fresh chat streaming turn so the thinking timer resets to 0 for the
        // thank-you turn. The server reuses the generic thinking/content stream events
        // for thank-you generation, so without a fresh turn those events keep writing
        // to the already-finished landing-page bubble (stale streamMsgId + stale
        // startedAt) — which is why the timer appeared to continue from the LP count.
        // onThankYouResult finalizes this bubble and stops the timer.
        beginStreaming()
        await landingPageHook.regenerateThankYou(prompt)
    }, [landingPageHook, isExternalMode, beginStreaming])

    // The page's record is the authority once it names a real cause; the local failure only covers the
    // moment between a failure event and the page re-read, or a page with no record yet.
    const storedFailure = landingPageHook.landingPage?.GenerationFailure
        ?? toGenerationFailure(landingPageHook.landingPage?.GenerationError)
    const generationFailure = storedFailure && storedFailure.Code !== 'Unknown'
        ? storedFailure
        : generationRequestError ?? storedFailure ?? null

    return {
        // Chat state
        messages: assetChat.messages,
        generating: assetChat.generating || generationActive,
        hasGenerated: !!landingPageHook.landingPage,
        // Chat input gated until the session id is captured (after first generation)
        chatReady: !landingPageHook.landingPage || !!assetChat.sessionId,

        // Multi-user turn-lock — expose so the LP builder can compute
        // "locked by other" against the current user id and pass
        // lockedByUserName into WebsterChatInput. turnUserId is null when
        // turnState is Idle, so a simple !== currentUserId check suffices.
        turnState: assetChat.turnState,
        turnUserId: assetChat.turnUserId,
        turnUserName: assetChat.turnUserName,

        // Landing page state
        landingPage: landingPageHook.landingPage,
        setLandingPage: landingPageHook.setLandingPage,
        html: landingPageHook.html,
        loading: landingPageHook.loading,
        context: landingPageHook.context,
        contextLoading: landingPageHook.contextLoading,
        sessionId: assetChat.sessionId,

        // The design round Webster is waiting on, drawn as the live card in the chat.
        openDesignRound,

        // Thank you page state
        thankYouHtml: landingPageHook.thankYouHtml,
        thankYouGenerating,
        loadThankYouDraft: landingPageHook.loadThankYouDraft,
        updateThankYouSettings: landingPageHook.updateThankYouSettings,
        updatePageMeta: landingPageHook.updatePageMeta,
        regenerateThankYou,
        rebuildWithCurrentBrand,
        // Nudge to generate a thank-you page whenever this LP has a draft but no
        // thank-you page, and one isn't already being generated. Derived from
        // landing-page state on every render rather than pushed from the server on
        // a chat turn: the API only computed it inside the chat-message path, so it
        // never fired on first generation (the moment it's actually for) and was
        // lost on reload. Every path that changes either term already reloads the
        // LP — first-gen and rebuilds via onStreamResult, thank-you completion via
        // onThankYouResult/onThankYouRebuilt, and mount via the init effect — so
        // this self-heals, including re-offering the nudge if generation failed (ORB-456).
        suggestThankYouGeneration: !!(
            landingPageHook.landingPage?.DraftBlobPath
            && !landingPageHook.landingPage?.ThankYouDraftBlobPath
            && !thankYouGenerating
        ),
        canGenerateThankYou: !!(landingPageHook.landingPage?.DraftBlobPath && (landingPageHook.landingPage.ThankYouGenerationStatus === 'Idle' || !landingPageHook.landingPage.ThankYouGenerationStatus)),

        // Generation status
        generationStatus: landingPageHook.landingPage?.GenerationStatus ?? null,
        generationFailure,
        generationError: generationFailure?.Code ?? null,
        thankYouGenerationStatus: landingPageHook.landingPage?.ThankYouGenerationStatus ?? null,
        thankYouGenerationError: landingPageHook.landingPage?.ThankYouGenerationError ?? null,
        thankYouGenerationFailure: landingPageHook.landingPage?.ThankYouGenerationFailure
            ?? toGenerationFailure(landingPageHook.landingPage?.ThankYouGenerationError),

        // Actions
        startGeneration,
        sendMessage,
        retryGeneration,
        loadPreview,
        updateSettings: landingPageHook.updateSettings,
        applyFont: landingPageHook.applyFont,
        applyFontFromChat,
        updateContext: landingPageHook.updateContext,
        loadContext: landingPageHook.loadContext,
        deleteLandingPage: landingPageHook.deleteLandingPage,
    }
}
